import {
  ChangeDetectionStrategy, Component, EventEmitter,
  Input, OnChanges, Output, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ExecutiveItem } from '../../models/pcheck.model';
import {
  browserTimezone, dateKeyInTimeZone, execTimezoneAbbr, formatSlotDateTimeInZone,
  resolveExecTimezone,
} from '../../helpers/booking-timezone';

type WindowFilter = 7 | null;

@Component({
  selector: 'app-executive-picker',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './executive-picker.component.html',
  styleUrls: ['./executive-picker.component.scss'],
})
export class ExecutivePickerComponent implements OnChanges {
  /** Recommended executives (subset with available slots). */
  @Input() executives: ExecutiveItem[] = [];
  /** Full list for "See all" view. Falls back to executives if not provided. */
  @Input() allExecutives?: ExecutiveItem[];
  @Input() disabled = false;
  /** Program-wide display zone. Set for Impact Pulse Check (the event's
   *  timezone) so EVERY tile renders in it; leave unset for Pulse Check, where
   *  each tile uses its own executive's zone. */
  @Input() eventTzIana?: string;

  @Output() executiveSelected = new EventEmitter<ExecutiveItem>();

  readonly showAll          = signal(false);
  readonly windowFilter     = signal<WindowFilter>(null);
  readonly showCustomInput  = signal(false);
  readonly customFrom       = signal('');
  readonly customTo         = signal('');
  readonly expandedSlugs    = signal<Set<string>>(new Set());

  /** Zone used to bucket "today" / date-range filters: the event zone when one
   *  is set (Impact), else the viewer's calendar. Slot *times* use _tzFor(). */
  private get _tz(): string { return this.eventTzIana || browserTimezone(); }

  /** Display zone for one tile: the program-wide event zone when set (Impact),
   *  else the executive's own configured zone (Pulse Check), else PST. */
  private _tzFor(exec: ExecutiveItem): string {
    return this.eventTzIana || resolveExecTimezone(exec);
  }

  ngOnChanges(): void {
    this.showAll.set(false);
    this.windowFilter.set(null);
    this.showCustomInput.set(false);
    this.customFrom.set('');
    this.customTo.set('');
    this.expandedSlugs.set(new Set());
  }

  // ── Derived ────────────────────────────────────────────────────────────────

  get availableRecommendations(): ExecutiveItem[] {
    return this.executives.filter(e => (e.available_slots ?? 0) > 0);
  }

  get execsForAllView(): ExecutiveItem[] {
    const list = this.allExecutives ?? this.executives;
    const wf   = this.windowFilter();
    const from = this.customFrom();
    const to   = this.customTo();

    if (!wf && !from) return list;

    const tz = this._tz;
    const todayKey = dateKeyInTimeZone(new Date(), tz);

    return list.map(exec => {
      if (!exec.next_slots) return exec;
      let filtered = exec.next_slots;

      if (wf) {
        const cutoff = this._addDays(todayKey, wf);
        filtered = filtered.filter(s => {
          const k = dateKeyInTimeZone(new Date(s), tz);
          return k >= todayKey && k <= cutoff;
        });
      } else if (from && to) {
        filtered = filtered.filter(s => {
          const k = dateKeyInTimeZone(new Date(s), tz);
          return k >= from && k <= to;
        });
      }

      return { ...exec, next_slots: filtered, available_slots: filtered.length };
    }).filter(e => (e.available_slots ?? 0) > 0);
  }

  get filterLabel(): string {
    const wf   = this.windowFilter();
    const from = this.customFrom();
    const to   = this.customTo();
    if (wf) return `Next ${wf} days`;
    if (from && to) return `${from} – ${to}`;
    return '';
  }

  slotDisplayData(exec: ExecutiveItem): { visible: string[]; total: number } {
    const slots = exec.next_slots ?? [];
    const isExpanded = this.expandedSlugs().has(exec.cp_user_slug);
    return {
      total:   slots.length,
      visible: isExpanded ? slots : slots.slice(0, 3),
    };
  }

  /** Slot times never render in the viewer's zone: the event zone for Impact,
   *  the executive's own zone for Pulse Check (see _tzFor). */
  formatSlot(isoUtc: string, exec: ExecutiveItem): string {
    return formatSlotDateTimeInZone(isoUtc, this._tzFor(exec));
  }

  /** Short zone indicator shown on the card, e.g. "PST" / "CDT". */
  execTimezoneLabel(exec: ExecutiveItem): string {
    return execTimezoneAbbr(this._tzFor(exec));
  }

  isExpanded(slug: string): boolean {
    return this.expandedSlugs().has(slug);
  }

  // ── Handlers ───────────────────────────────────────────────────────────────

  onSelect(exec: ExecutiveItem): void {
    if (!this.disabled) this.executiveSelected.emit(exec);
  }

  onShowAll(): void     { this.showAll.set(true); this.windowFilter.set(null); this.customFrom.set(''); this.customTo.set(''); }
  onShowRecs(): void    { this.showAll.set(false); this.windowFilter.set(null); }
  onWindowFilter(n: WindowFilter): void { this.showAll.set(true); this.windowFilter.set(n); this.showCustomInput.set(false); }
  onShowCustom(): void  { this.showCustomInput.set(true); }
  onCancelCustom(): void { this.showCustomInput.set(false); }

  onCustomFromChange(event: Event): void {
    this.customFrom.set((event.target as HTMLInputElement).value);
  }
  onCustomToChange(event: Event): void {
    this.customTo.set((event.target as HTMLInputElement).value);
  }
  onApplyCustom(): void {
    this.showAll.set(true);
    this.windowFilter.set(null);
    this.showCustomInput.set(false);
  }

  toggleSlotExpand(slug: string, event: Event): void {
    event.stopPropagation();
    this.expandedSlugs.update(set => {
      const next = new Set(set);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });
  }

  private _addDays(ymd: string, n: number): string {
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d + n));
    return [dt.getUTCFullYear(), String(dt.getUTCMonth()+1).padStart(2,'0'), String(dt.getUTCDate()).padStart(2,'0')].join('-');
  }
}
